import PropTypes from 'prop-types';
import { forwardRef, useMemo, useState } from 'react';
import { cn } from 'Utils/cn';
import {
    useFloating,
    useClick,
    useDismiss,
    useRole,
    useInteractions,
    flip,
    useMergeRefs,
    autoUpdate,
    size,
    offset,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import OutlineChevronDown from 'Components/Atoms/OutlineIcons/OutlineChevronDown';
import selectVariants from 'Components/Atoms/Select/SelectVariants';
import FloatLabel from 'Components/Atoms/FloatLabel/FloatLabel';
import ErrorMessage from 'Components/Atoms/ErrorMessage/ErrorMessage';

const TAXES_ORDER = ['ESP_IVA', 'ESP_IRPF'];

const SelectSingleTaxField = forwardRef(function SelectSingleTaxField(props, ref) {
    const { value, disabled, onChange, className, placeholder, excludeIds = [], label, error, invoiceTaxes } = props;

    const hasError = !!error;

    const [open, setOpen] = useState(false);
    const { refs, floatingStyles, context, strategy, x, y } = useFloating({
        open,
        onOpenChange: setOpen,
        whileElementsMounted: autoUpdate,
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [
            offset(4),
            flip(),
            size({
                apply({ _rects, elements, availableHeight }) {
                    Object.assign(elements.floating.style, {
                        maxHeight: `${Math.min(availableHeight - 20, 520)}px`,
                    });
                },
            }),
        ],
    });

    const { getFloatingProps, getReferenceProps } = useInteractions([
        useClick(context),
        useRole(context, { role: 'listbox' }),
        useDismiss(context),
    ]);

    const groups = useMemo(() => {
        const grouped = invoiceTaxes.reduce((acc, tax) => {
            if (!acc[tax.group]) {
                acc[tax.group] = [];
            }
            acc[tax.group].push(tax);
            return acc;
        }, {});

        return Object.entries(grouped)
            .map(([group, taxes]) => ({
                id: group,
                name: taxes[0]?.group_name || group,
                taxes: taxes,
            }))
            .sort((a, b) => {
                const indexA = TAXES_ORDER.indexOf(a.id);
                const indexB = TAXES_ORDER.indexOf(b.id);
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                return indexA - indexB;
            });
    }, [invoiceTaxes]);

    const [currentGroup, setCurrentGroup] = useState(() => {
        const defaultId = groups[0]?.id ?? null;

        if (!value) {
            return defaultId;
        }

        const selectedGroup = groups.find((group) => group.taxes.some((t) => t.id === value));

        return selectedGroup?.id ?? defaultId;
    });

    const selectedTax = invoiceTaxes.find((tax) => tax.id === value);
    const allItems = groups.find((g) => g.id === currentGroup)?.taxes ?? [];
    const items = allItems.filter((tax) => !excludeIds.includes(tax.id));

    const mergedRef = useMergeRefs([ref, refs.setReference]);

    const handleSelectTax = (tax) => {
        onChange(tax.id);
        setOpen(false);
    };

    return (
        <div className={cn('relative min-w-0 w-full max-w-full group', className)}>
            <FloatLabel label={label} isOpen={open} hasError={hasError} />

            <button
                type="button"
                ref={mergedRef}
                disabled={disabled}
                className={cn(
                    selectVariants({ size: 'normal', status: hasError ? 'error' : 'default' }),
                    'w-full flex items-center gap-1',
                    open && 'border-primary ring-2 ring-primary/20',
                    !selectedTax && 'text-dark-blue/50',
                )}
                aria-haspopup="listbox"
                aria-expanded={open}
                {...getReferenceProps()}
            >
                <div className="truncate grow min-w-0">{selectedTax ? selectedTax.name : placeholder}</div>
                <OutlineChevronDown size={20} className="shrink-0 text-dark-blue" color="currentColor" />
            </button>

            <AnimatePresence>
                {open && (
                    <motion.div
                        ref={refs.setFloating}
                        style={{
                            ...floatingStyles,
                            position: strategy,
                            top: y ?? 0,
                            left: x ?? 0,
                        }}
                        className="w-[280px] overflow-x-hidden overflow-y-auto scrollbar-thin rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none text-sm z-dropdown"
                        tabIndex={-1}
                        {...getFloatingProps()}
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.15, ease: 'easeOut' }}
                    >
                        <div
                            className={cn(
                                'sticky top-0 z-10 flex items-center gap-0.5 py-1 px-1.5 bg-gray-100 overflow-x-auto scrollbar-thin',
                            )}
                        >
                            {groups.map((group) => (
                                <button
                                    type="button"
                                    key={group.id}
                                    className={cn(
                                        'whitespace-nowrap text-xs px-3 py-1.5 font-semibold rounded-sm',
                                        currentGroup === group.id && 'font-semibold bg-white text-dark-blue shadow',
                                    )}
                                    onClick={(ev) => {
                                        setCurrentGroup(group.id);
                                        const target = ev.currentTarget;
                                        target.scrollIntoView({
                                            behavior: 'smooth',
                                            block: 'nearest',
                                            inline: 'center',
                                        });
                                    }}
                                >
                                    {group.name}
                                </button>
                            ))}
                        </div>

                        <ul className="p-1.5 min-h-12">
                            {items.map((tax) => (
                                <li
                                    key={tax.id}
                                    className={cn(
                                        'cursor-pointer select-none py-2 px-3 rounded-sm transition-colors',
                                        value === tax.id
                                            ? 'bg-primary/10 text-primary font-semibold'
                                            : 'hover:bg-gray-100',
                                    )}
                                    role="option"
                                    aria-selected={value === tax.id}
                                    onClick={() => handleSelectTax(tax)}
                                >
                                    {tax.name}
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                )}
            </AnimatePresence>

            <ErrorMessage message={error} />
        </div>
    );
});

export default SelectSingleTaxField;

SelectSingleTaxField.propTypes = {
    disabled: PropTypes.bool,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    className: PropTypes.string,
    placeholder: PropTypes.string,
    excludeIds: PropTypes.arrayOf(PropTypes.string),
    label: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    invoiceTaxes: PropTypes.array.isRequired,
};
